import '../../styles/SingleDatePicker.less';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useState, useRef, useEffect } from 'react';
import { HSNewCalendarIcon } from './Icons';
import { Tooltip, DatePicker, ConfigProvider } from 'antd';
import { getCurrentUser } from '@vantiva-sa/homesight_humaui/Store/authStore';
import useUserInfo from '../Hooks/useUserInfo';
import { useSelector } from 'react-redux';
import { getDateFormat } from '@vantiva-sa/homesight_humaui/Utilities/DateTime';
import enGB from 'antd/lib/locale/en_GB'; // Import Ant Design's English GB locale
import weekday from 'dayjs/plugin/weekday';
import localeData from 'dayjs/plugin/localeData';
import updateLocale from 'dayjs/plugin/updateLocale'; // Import the plugin
import useTranslatedDateFormat from '../Hooks/useTranslatedDateFormat';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(updateLocale);

dayjs.updateLocale('en', {
  weekStart: 1, // Monday
});

// Get date format based on user locale
const getDateFormatByLocale = (locale) => {
  const formatter = new Intl.DateTimeFormat(locale);
  const parts = formatter.formatToParts(new Date());
  return parts
    .map((part) => {
      switch (part.type) {
        case 'day':
          return 'DD';
        case 'month':
          return 'MM';
        case 'year':
          return 'YYYY';
        default:
          return part.value;
      }
    })
    .join('');
};

// Function to remove default tooltips
const removeDefaultTooltips = () => {
  const pickerCells = document.querySelectorAll('.ant-picker-cell');
  pickerCells.forEach((cell) => {
    cell.removeAttribute('title'); // Remove default title attribute
  });
};

export const SingleDatePicker = (props) => {
  const currentUser = useSelector(getCurrentUser);
  const { data: userInfo } = useUserInfo({ useruuid: currentUser?.useruuid });

  // Determine user locale with fallback
  const userLocale = userInfo?.locale || 'en-US';
  const dateFormatUserLocale = getDateFormatByLocale(userLocale);

  const {
    onDateSelect,
    required,
    timeZone,
    defaultValue,
    defaultText,
    name,
    className = '',
    shouldDisablePastDates = false,
  } = props;

  const datePickerRef = useRef(null);
  const wrapperRef = useRef(null); // Ref for the wrapper div
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(defaultValue);
  const [initialDefaultDate, setInitialDefaultDate] = useState(defaultValue);

  useEffect(() => {
    if (defaultValue && !initialDefaultDate) {
      setInitialDefaultDate(defaultValue);
    }
  }, [defaultValue, initialDefaultDate]);

  const initialDateInTZ = initialDefaultDate ? dayjs(initialDefaultDate).tz(timeZone) : null;

  const disableBeforeInitialDate = (current) => {
    if (!shouldDisablePastDates) return false;
    if (!initialDateInTZ) return false;
    return current.isBefore(initialDateInTZ, 'day');
  };

  const handleDateSelect = (value) => {
    setPickerOpen(false);
    setSelectedDate(value);
    onDateSelect(value);
  };

  const handleOnBlur = () => {
    setPickerOpen(false);
  };

  useEffect(() => {
    setSelectedDate(defaultValue);
  }, [defaultValue]);

  useEffect(() => {
    if (datePickerRef.current) {
      datePickerRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (pickerOpen) {
      // Remove tooltips immediately when the picker opens
      removeDefaultTooltips();
    }
  }, [pickerOpen]);

  const handleWrapperClick = () => {
    if (!pickerOpen) {
      setPickerOpen(true);
    }
  };

  const handleWrapperBlur = (event) => {
    // Check if the blur event is happening outside the DatePicker and wrapper
    const isDatePickerButton =
      event.target.classList.contains('ant-picker-header-super-prev-btn') ||
      event.target.classList.contains('ant-picker-header-super-next-btn') ||
      event.target.closest('.ant-picker-panel');
    if (
      !wrapperRef.current.contains(event.target) &&
      datePickerRef.current !== event.target &&
      !isDatePickerButton
    ) {
      handleOnBlur(); // Call handleOnBlur if clicked outside DatePicker and wrapper
    }
  };

  const onOpenChangePicker = (pickerOpen) => {
    if (!pickerOpen) {
      setPickerOpen(false);
    }
  };

  const handlePanelChange = () => {
    setTimeout(() => {
      removeDefaultTooltips(); // Remove tooltips when month or year changes
    }, 500);
  };

  const dateFormat = selectedDate ? getDateFormat(selectedDate, timeZone) : null;
  const translatedDateFormat = useTranslatedDateFormat(dateFormat);
  const day = translatedDateFormat?.day || defaultText;
  const todayInUserTZ = dayjs().tz(timeZone);

  return (
    <div
      className="single-datepicker-wrapper"
      ref={wrapperRef}
      onClick={handleWrapperClick}
      onBlur={handleWrapperBlur}
    >
      <div className="single-datepicker-content">
        <div className="single-datepicker-date">
          <div className="single-datepicker-calendar">
            <div className="action">
              <ConfigProvider locale={enGB}>
                <DatePicker
                  ref={datePickerRef}
                  className="single-datepicker"
                  required={required}
                  allowClear={false}
                  open={pickerOpen}
                  name={name}
                  disabledDate={disableBeforeInitialDate}
                  onPanelChange={handlePanelChange}
                  format={dateFormatUserLocale}
                  onChange={(value) => handleDateSelect(value)}
                  onOpenChange={onOpenChangePicker}
                  value={
                    selectedDate && dayjs(selectedDate).isValid()
                      ? dayjs(selectedDate).tz(timeZone)
                      : null
                  }
                  showNow={false}
                  style={{ width: '100%', zIndex: 1000 }}
                  placement="bottomLeft"
                  classNames={{ popup: { root: `single-datepicker-dropdown ${className}`.trim() } }}
                  cellRender={(current, info) => {
                    if (info.type === 'date') {
                      const isToday = current.isSame(todayInUserTZ, 'day');
                      return (
                        <Tooltip
                          title={`${current.format(dateFormatUserLocale)}`}
                          placement="top"
                          mouseEnterDelay={0.3}
                        >
                          <div
                            className={`ant-picker-cell-inner ${isToday ? 'user-tz-today-cell' : ''}`}
                          >
                            {current.date()}
                          </div>
                        </Tooltip>
                      );
                    }
                    return info.originNode;
                  }}
                />
              </ConfigProvider>
              <div className="flex-center">
                <HSNewCalendarIcon />
              </div>
            </div>
          </div>
          <span className="single-datepicker-day">{day}</span>
          &nbsp;{translatedDateFormat?.dateFormat ? translatedDateFormat.dateFormat : ''}
        </div>
      </div>
    </div>
  );
};
